import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";

export type ContinuousVoiceStatus =
  | "off"
  | "starting"
  | "listening"
  | "speech"
  | "pause_pending"
  | "finalizing"
  | "dispatching"
  | "queued"
  | "error";

/** What live mode has heard but not yet sent, as the subtitle shows it. */
export type LiveVoicePendingText = {
  /** Segments committed and waiting on their final, oldest first, each by
   *  its native segment key. */
  committing: Array<{ key: string; text: string }>;
  /** The follow-up held until the agent's turn ends. */
  followup: string;
  /** A segment being routed, or a short fragment held for the words that
   *  may follow it. */
  fragment: string;
};

export const NO_PENDING_TEXT: LiveVoicePendingText = {
  committing: [],
  followup: "",
  fragment: "",
};

type ContinuousVoiceState = {
  enabled: boolean;
  /**
   * The continuous session owns (or is acquiring) the native microphone.
   * Set with `enable()` — the start command is already in flight then — and
   * cleared only where the native capture truly ends: `disable()` and
   * `markCaptureEnded()` (the hard-failure path, which stops the sidecar).
   * A soft failure (`fail()` alone, e.g. a commit ack timeout or a CLI-switch
   * error) keeps it true because the sidecar keeps capturing.
   */
  captureLive: boolean;
  status: ContinuousVoiceStatus;
  partialText: string;
  stableText: string;
  currentSegmentId: string | null;
  segmentStartedAt: number | null;
  lastSpeechAt: number | null;
  lastPartialChangedAt: number | null;
  queuedTurnCount: number;
  lastDispatchStartedText: string | null;
  lastDispatchStartedAt: number | null;
  activeDispatchId: string | null;
  lastDispatchedText: string | null;
  lastDispatchedAt: number | null;
  lastSuppressedTtsEchoText: string | null;
  lastSuppressedTtsEchoAt: number | null;
  lastSuppressedTtsEchoCaptureId: string | null;
  lastSuppressedTtsEchoSegmentId: number | null;
  pendingText: LiveVoicePendingText;
  /** Windows showing an open conversation frame, by window label. */
  conversationOpenIn: string[];
  lastError?: string;
  startSegment: (at?: number) => void;
  updatePartial: (text: string, at?: number) => void;
  setStatus: (status: ContinuousVoiceStatus) => void;
  enable: () => void;
  disable: () => void;
  markCaptureEnded: () => void;
  markSuppressedTtsEcho: (
    text: string,
    identity?: { captureId?: string; segmentId?: number },
    at?: number,
  ) => void;
  markDispatchStarted: (
    text: string,
    at?: number,
    preserveCapture?: boolean,
  ) => string;
  markDispatched: (
    text: string,
    queued: boolean,
    options?: {
      at?: number;
      dispatchId?: string;
      updatePulse?: boolean;
      updateText?: boolean;
    },
  ) => void;
  fail: (error: string) => void;
};

function nextSegmentId(): string {
  return `voice-seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

let suppressNextDictationFinal = false;
let suppressNextTimer: ReturnType<typeof setTimeout> | null = null;
let dispatchSequence = 0;

function nextDispatchId(at: number): string {
  return `voice-dispatch-${at}-${++dispatchSequence}`;
}

export function suppressNextOneShotFinal(): void {
  suppressNextDictationFinal = true;
  if (suppressNextTimer) clearTimeout(suppressNextTimer);
  suppressNextTimer = setTimeout(() => {
    suppressNextDictationFinal = false;
    suppressNextTimer = null;
  }, 6000);
}

export function consumeSuppressedOneShotFinal(): boolean {
  if (!suppressNextDictationFinal) return false;
  suppressNextDictationFinal = false;
  if (suppressNextTimer) {
    clearTimeout(suppressNextTimer);
    suppressNextTimer = null;
  }
  return true;
}

export const useContinuousVoice = create<ContinuousVoiceState>()(
  mirroredStore("continuousVoice", (set) => ({
    enabled: false,
    captureLive: false,
    status: "off",
    partialText: "",
    stableText: "",
    currentSegmentId: null,
    segmentStartedAt: null,
    lastSpeechAt: null,
    lastPartialChangedAt: null,
    queuedTurnCount: 0,
    lastDispatchStartedText: null,
    lastDispatchStartedAt: null,
    activeDispatchId: null,
    lastDispatchedText: null,
    lastDispatchedAt: null,
    lastSuppressedTtsEchoText: null,
    lastSuppressedTtsEchoAt: null,
    lastSuppressedTtsEchoCaptureId: null,
    lastSuppressedTtsEchoSegmentId: null,
    pendingText: NO_PENDING_TEXT,
    conversationOpenIn: [],

    enable: () =>
      set({
        enabled: true,
        captureLive: true,
        status: "starting",
        partialText: "",
        stableText: "",
        currentSegmentId: nextSegmentId(),
        segmentStartedAt: null,
        lastSpeechAt: null,
        lastPartialChangedAt: null,
        lastDispatchStartedText: null,
        lastDispatchStartedAt: null,
        activeDispatchId: null,
        lastDispatchedAt: null,
        lastSuppressedTtsEchoText: null,
        lastSuppressedTtsEchoAt: null,
        lastSuppressedTtsEchoCaptureId: null,
        lastSuppressedTtsEchoSegmentId: null,
        pendingText: NO_PENDING_TEXT,
        // A window that closed mid-session never withdrew its label.
        conversationOpenIn: [],
        lastError: undefined,
      }),

    disable: () =>
      set({
        enabled: false,
        captureLive: false,
        status: "off",
        partialText: "",
        stableText: "",
        currentSegmentId: null,
        segmentStartedAt: null,
        lastSpeechAt: null,
        lastPartialChangedAt: null,
        queuedTurnCount: 0,
        lastDispatchStartedText: null,
        lastDispatchStartedAt: null,
        activeDispatchId: null,
        lastDispatchedAt: null,
        lastSuppressedTtsEchoText: null,
        lastSuppressedTtsEchoAt: null,
        lastSuppressedTtsEchoCaptureId: null,
        lastSuppressedTtsEchoSegmentId: null,
        pendingText: NO_PENDING_TEXT,
        conversationOpenIn: [],
        lastError: undefined,
      }),

    setStatus: (status) => set({ status }),

    markCaptureEnded: () => set({ captureLive: false }),

    startSegment: (at = Date.now()) =>
      set((state) => ({
        status: "speech",
        currentSegmentId: state.currentSegmentId ?? nextSegmentId(),
        segmentStartedAt: state.segmentStartedAt ?? at,
        lastSpeechAt: at,
        activeDispatchId: null,
      })),

    updatePartial: (text, at = Date.now()) =>
      set((state) => {
        // Interim hypotheses often repeat verbatim: skip the redundant set
        // (subscriber re-render + mirror emit) while keeping the recency
        // timestamp fresh for voiceWakeBusy()'s 1s activity window.
        // Returning the same reference makes zustand bail out entirely.
        if (
          state.partialText === text &&
          state.lastPartialChangedAt !== null &&
          at - state.lastPartialChangedAt < 1000
        ) {
          return state;
        }
        return {
          partialText: text,
          stableText: text,
          lastPartialChangedAt: at,
          activeDispatchId: null,
        };
      }),

    markSuppressedTtsEcho: (text, identity, at = Date.now()) =>
      set({
        lastSuppressedTtsEchoText: text,
        lastSuppressedTtsEchoAt: at,
        lastSuppressedTtsEchoCaptureId: identity?.captureId ?? null,
        lastSuppressedTtsEchoSegmentId: identity?.segmentId ?? null,
      }),

    markDispatchStarted: (text, at = Date.now(), preserveCapture = false) => {
      const activeDispatchId = nextDispatchId(at);
      set({
        ...(preserveCapture
          ? {}
          : { status: "dispatching" as const, activeDispatchId }),
        lastDispatchStartedText: text,
        lastDispatchStartedAt: at,
      });
      return activeDispatchId;
    },

    markDispatched: (text, queued, options) =>
      set((state) => {
        const ownsVisibleState =
          state.status === "dispatching" &&
          (options?.dispatchId === undefined ||
            state.activeDispatchId === options.dispatchId);
        return {
          ...(ownsVisibleState
            ? {
                status: "listening" as const,
                partialText: "",
                stableText: "",
                currentSegmentId: nextSegmentId(),
                segmentStartedAt: null,
                lastSpeechAt: null,
                lastPartialChangedAt: null,
                activeDispatchId: null,
              }
            : {}),
          ...(options?.updateText === false
            ? {}
            : { lastDispatchedText: text }),
          ...(options?.updatePulse === false
            ? {}
            : {
                lastDispatchedAt: Math.max(
                  options?.at ?? Date.now(),
                  (state.lastDispatchedAt ?? 0) + 1,
                ),
              }),
          queuedTurnCount: queued
            ? state.queuedTurnCount + 1
            : state.queuedTurnCount,
        };
      }),

    fail: (error) =>
      set({
        status: "error",
        lastError: error,
      }),
  })),
);

/** The microphone was given up between two states: live mode was switched
 *  off or its capture failed. A soft failure keeps capturing, so it is not. */
export function continuousVoiceSessionEnded(
  state: Pick<ContinuousVoiceState, "captureLive">,
  previous: Pick<ContinuousVoiceState, "captureLive">,
): boolean {
  return previous.captureLive && !state.captureLive;
}

export function isContinuousVoiceActive(): boolean {
  return useContinuousVoice.getState().enabled;
}

/**
 * True while the continuous session owns the native microphone — an explicit
 * fact, not an inference from `status`. `isContinuousVoiceActive` reflects
 * the user-facing toggle and stays true after a hard capture failure so the
 * error UI persists; this selector goes false then (`markCaptureEnded`),
 * which frees one-shot dictation to use the sidecar again. A soft failure
 * (`fail()` alone) leaves it true because the sidecar keeps capturing.
 * `captureLive` implies `enabled`: `enable()` is its only true-setter and
 * `disable()` is the only place `enabled` goes false, clearing both.
 */
export function isContinuousCaptureAlive(): boolean {
  return useContinuousVoice.getState().captureLive;
}
